import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function DustBowlArticle({}) {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>
          An Overview of Farmland, Crops, and Property During the Dust Bowl
        </h1>
        <ArticleAuthor />
        <p>
          The 1930s was a period of great struggle in the United States. The
          Great Depression was taking a toll on the nation's economy and left
          many people in dire straits financially. The farmers living in the
          Great Plains were hit twice as hard as others in the nation due to a
          decades-long drought, erosion of the soil, and severe dust storms.
          This region was known as the Dust Bowl, and it would become one of the
          worst environmental disasters in the history of the United States.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Dust Bowl Causes and Effects
        </h2>
        <p>
          There are many factors, starting with Manifest Destiny, that led to
          the environmental disaster that was the Dust Bowl. When Americans
          moved to expand the country's boundaries westward, they were
          unfamiliar with the land in the Great Plains and how to manage it.
          These inexperienced farmers believed that "Rain follows the plow,"
          thinking that once they established farms, the rain would naturally
          come. As people migrated to the region, it experienced an unnatural
          amount of rain that led farmers to believe that this was the normal
          state.
        </p>
        <p>
          At the same time, there was a growing need for wheat in Europe due to
          World War I, and farmers strove to meet that demand by plowing under
          the prairie grass to make room for wheat crops. When the Depression
          struck, the price of wheat dropped; in an effort to produce more crops
          to make a profit, farmers plowed more of the grassland. What they did
          not understand was the importance of the prairie grass to the soil.
          The grass helped to anchor the soil in place. As crops failed due to
          drought, the now dry and loose soil was susceptible to wind erosion
          and was easily lifted and blown away.
        </p>
        <p>
          As a result, tremendous{" "}
          <a
            rel="nofollow"
            href="http://ocp.ldeo.columbia.edu/res/div/ocp/drought/dust_storms.shtml"
          >
            dust storms
          </a>{" "}
          plagued the region and beyond. The storms displaced topsoil, making
          the land unfarmable, and suffocated livestock. People had to wear
          masks to breathe, and children and adults developed dust pneumonia
          from inhaling the dry soil. While some farmers held on to their land,
          others could not do so and either lost or sold it. As the economy
          suffered, an estimated 2.5 million people migrated west as
          environmental refugees.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://ny.pbslearningmedia.org/resource/ecological-disaster-ken-burns-dust-bowl/ken-burns-the-dust-bowl/"
            >
              A Man-Made Ecological Disaster
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.kshs.org/kansapedia/dust-bowl/12040"
            >
              Dust Bowl
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.kennedy-center.org/education/resources-for-educators/classroom-resources/media-and-interactives/media/literary-arts/john-steinbeck--the-grapes-of-wrath/chapters/drought-and-devistation/"
            >
              Drought and Devastation: Causes of the Dust Bowl Tragedy
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.tucson.ars.ag.gov/isco/isco18/abstracts/Gill_the%20multiple%20causes%20of%20the%20dust%20bowl.pdf"
            >
              Multiple Causes of the Dust Bowl
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          When Was the Dust Bowl?
        </h2>
        <p>
          The{" "}
          <a
            rel="nofollow"
            href="https://www.loc.gov/classroom-materials/united-states-history-primary-source-timeline/great-depression-and-world-war-ii-1929-1945/dust-bowl/"
          >
            Dust Bowl
          </a>{" "}
          took place during the 1930s in a period called the Dirty Thirties. It
          started with the onset of a severe drought in 1930 and continued until
          the end of 1939, with the return of rainfall. Although the drought
          started in 1930, the first large dust storm did not occur until 1931.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.legendsofamerica.com/20th-dustbowl/"
            >
              Dust Bowl Days: The Dirty Thirties
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.neh.gov/humanities/2012/novemberdecember/feature/children-the-dust"
            >
              Children of the Dust
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://livinghistoryfarm.org/farminginthe30s/water_02.html"
            >
              Farming in the 1930s: The Dust Bowl
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.americaslibrary.gov/es/ok/es_ok_dustbowl_1.html"
            >
              The Dust Bowl of{" "}
            </a>
            <a href="https://www.realestateagents.com/ok/oklahoma-city">
              Oklahoma
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Frequency and Severity of the Storms
        </h2>
        <p>
          Every year during the Dirty Thirties saw multiple dust storms ravage
          the Plains. These storms seemed to worsen each year. In 1932, 14 dust
          storms hit the Midwestern and Southern Plains. In 1933, the number of
          storms increased to 38. Although 1934 experienced only 22 storms, they
          were more intense. For two days in May 1934, the most severe storm to
          that point carried 350 million tons of dust in the air, blowing it as
          far as across the East Coast and 300 miles out into the Atlantic
          Ocean.
        </p>
        <p>
          Between 1935 and 1938, the{" "}
          <a
            rel="nofollow"
            href="https://www.science.smith.edu/climatelit/the-dust-bowl/"
          >
            Dust Bowl
          </a>{" "}
          experienced the worst storms. In 1935, for example, there were 40{" "}
          <a
            rel="nofollow"
            href="https://www.towntalkradio.com/on-this-day-in-1934-dust-storm-sweeps-from-great-plains-to-eastern-states/"
          >
            dust storms
          </a>{" "}
          and 908 hours of blackout conditions, including an event that would
          become known as "Black Sunday." During the final year of the Dust
          Bowl, the number of storms dropped to 30, and for two years after,
          dust storms continued, with both 1940 and 1941 experiencing 17 storms.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.okhistory.org/publications/enc/entry.php?entry=FA015"
            >
              The Encyclopedia of Oklahoma History and Culture
            </a>
          </li>
          <li>
            <a href="https://www.realestateagents.com/tx/austin">Texas</a>
            <a
              rel="nofollow"
              href="https://www.tshaonline.org/handbook/entries/dust-bowl"
            >
              {" "}
              State Historical Association: Dust Bowl
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://kinsleylibrary.info/wp-content/uploads/2014/10/Handi-facts.pdf"
            >
              Handy Dandy Dust Bowl Facts
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Black Sunday</h2>
        <p>
          Some of the more severe dust storms were characterized as black
          blizzards that darkened the sky with clouds of dust. These storms were
          so intense that even the interiors of tightly sealed homes were
          covered in layers of dust.
        </p>
        <p>
          On April 14, 1935, the worst of these black blizzards struck. A day
          that started with clear blue skies was dubbed{" "}
          <a
            rel="nofollow"
            href="https://www.smithsonianmag.com/smart-news/1000-mile-long-storm-showed-horror-life-dust-bowl-180962847/"
          >
            Black Sunday
          </a>{" "}
          when a 1,000-mile-long dust storm carrying roughly 300,000 tons of
          topsoil rolled across the Great Plains starting at the Oklahoma
          Panhandle. The storm had winds as fast as 60 mph, and it turned
          daylight into darkness that many would describe as darker than night.
          It was on Black Sunday that Robert Geiger, a reporter with The
          Associated Press, coined the phrase "Dust Bowl."
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.pbs.org/wgbh/americanexperience/features/dust-bowl-black-sunday/"
            >
              Surviving the Dust Bowl: Black Sunday
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.weather.gov/oun/events-19350414"
            >
              The Black Sunday Dust Storm of April 14, 1935
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://weather.ok.gov/index.php/news/article/black_sunday_remembered"
            >
              Black Sunday Remembered
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.history.com/news/remembering-black-sunday"
            >
              What Happened on Black Sunday?
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          New Deal Programs for Farmers
        </h2>
        <p>
          President Franklin D. Roosevelt's answer to the drought and dust
          storms came in the form of his New Deal programs. The{" "}
          <a
            rel="nofollow"
            href="https://www.seaford.k12.ny.us/cms/lib/NY01000674/Centricity/Domain/324/Alphabet%20Soup%20chart%20key%20PDF.pdf"
          >
            alphabet soup of programs
          </a>{" "}
          provided emergency relief to people suffering from the effects of the
          Depression and the Dust Bowl. Some of these programs were specific to
          Dust Bowl farmers and the environment. These programs included the
          Agricultural Adjustment Act of 1933, which paid farmers to reduce
          their crops, and the Emergency Farm Mortgage Act, which gave loans to
          farms at risk of foreclosure and extended repayment. Other New Deal
          programs established the Soil Conservation Service in 1935 and enacted
          the Soil Conservation and Domestic Allotment Act in 1936, which
          encouraged farmers to plant crops, such as grasses and beans, that
          helped build soil.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.georgiaencyclopedia.org/articles/business-economy/agricultural-adjustment-act/"
            >
              Agricultural Adjustment Act
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.fca.gov/about/history-of-fca">
              History of the FCA
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.nps.gov/home/learn/historyculture/ruralelect.htm"
            >
              Rural Electrification Act
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://fdr4freedoms.org/wp-content/themes/fdf4fdr/DownloadablePDFs/II_HopeRecoveryReform/03_ANewDealforFarmers.pdf"
            >
              A New Deal for Farmers
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Disaster Gives Way to Hope
        </h2>
        <p>
          The hunger, drought, dust-induced illness, and even an invasion of
          crickets became more than many could bear. More than a quarter-million
          individuals and families fled westward hoping to find work and a new
          life. Unfortunately, this did not resolve their plight, as work was
          scarce because of the sheer number of environmental refugees, many of
          whom settled in California. To make matters worse, they were also met
          with hostility and prejudice, as many Californians did not want them
          there.
        </p>
        <p>
          Although the drought and dust storms continued, many farmers decided
          to stay on their lands. Government programs began paying them to learn
          and use new farming techniques that conserved topsoil. And the Prairie
          States Forestry Project, originally called the{" "}
          <a
            rel="nofollow"
            href="http://www.fdrlibrary.marist.edu/daybyday/resource/march-1935-8/"
          >
            Shelterbelt Project
          </a>{" "}
          when it began 1934, planted trees to create windbreaks and reduce soil
          erosion. These efforts were effective; there was a 65% reduction in
          the loss of topsoil by 1938. While not perfect, this improvement gave
          farmers hope. The drought and dust storms, however, continued on until
          1939, when the rains returned, putting an end to the decade-long
          drought.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.nrcs.usda.gov/wps/portal/nrcs/detail/national/about/history/?cid=nrcs143_021392"
            >
              Honoring 86 Years of NRCS: A Brief History
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://history.house.gov/Historical-Highlights/1901-1950/Soil-Conservation-in-the-New-Deal-Congress/"
            >
              Soil Conservation in the New Deal Congress
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://drought.unl.edu/dustbowl/#Recovery">
              Coping and Recovering
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://apnep.nc.gov/blog/2017/10/16/dust-came-soil-conservation"
            >
              From Dust Came Soil Conservation
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://livingnewdeal.org/glossary/shelterbelt-project-1934/"
            >
              Shelterbelt Project
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Additional Reading on the Dust Bowl and Farmland
        </h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://library.uoregon.edu/ec/wguthrie/dustbowl.html"
            >
              The Dust Bowl and the Great Depression
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="http://www.digitalhistory.uh.edu/disp_textbook.cfm?smtID=2&amp;psid=3441"
            >
              The Farmers' Plight
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://sites.wp.odu.edu/great-depression/2019/03/20/dust-bowl/"
            >
              The Great Depression and the New Deal: Dust Bowl
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.adamshistory.org/index.php?option=com_content&amp;view=article&amp;id=18:dust-bowl&amp;catid=2&amp;Itemid=42"
            >
              The Dust Bowl Years
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.aaas.org/dust-bowl-wake-call-environmental-practices"
            >
              The Dust Bowl: A Wake-Up Call in Environmental Practices
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://fdr.blogs.archives.gov/2018/06/20/fdr-and-the-dust-bowl/"
            >
              FDR and the Dust Bowl
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.wbur.org/cognoscenti/2021/04/20/megadrought-climate-change-frederick-hewett"
            >
              What the Dust Bowl of the 1930s Can Teach Us About the Origins of
              a Looming Megadrought
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://migration.ucdavis.edu/rmn/more.php?id=1355"
            >
              Dust Bowl Migration
            </a>
          </li>
          <li>
            <a href="https://www.realestateagents.com/ia/des-moines">Iowa</a>
            <a
              rel="nofollow"
              href="https://iowaculture.gov/history/education/educator-resources/primary-source-sets/dust-bowl#:~:text=Conservation%20Efforts,and%20keep%20topsoil%20in%20place."
            >
              {" "}
              Culture: The Dust Bowl
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://coloradoencyclopedia.org/article/dust-bowl"
            >
              Encyclopedia Colorado: The Dust Bowl
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://historynewsnetwork.org/article/149432"
            >
              Lessons From the Dust Bowl
            </a>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "An Overview of Farmland, Crops, and Property During the Dust Bowl | RealEstateAgents.com"
        }
        description={
          "The Dustbowl of the 1930s changed the economic and cultural climate in the 1930s for generations to come. Review a brief history of events here."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/farms-crops-property-dust-bowl",
          },
          headline:
            "An Overview of Farmland, Crops, and Property During the Dust Bowl",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
